import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { FocusEvent, ReactNode } from "react";
import { useLabelLayout } from "./Internal/FormItemContext";
import { useIsFormSubmitted } from "./Internal/FormValidationContext";
import type { FieldStatus } from "./FormItemErrorProvider";
import { FormItemErrorProvider } from "./FormItemErrorProvider";
import { Tooltip } from "../Feedback/Tooltip";
import { headingFontStyle } from "../Theme/headingFontStyle";
import { useControlPresentation } from "../Controls/Internal/useControlPresentation";
import { DONE_TYPING_AFTER_MS } from "./Internal/typingPause";

/**
 * A form validation message: any renderable content (string, JSX, …) or
 * nothing. `string` is already part of `ReactNode`, so it needn't be spelled
 * out. The canonical type for every control's `error` / `warning` prop.
 */
export type ErrorMessageType = ReactNode | undefined;

/** A map of field key → its validation message. */
export type ErrorObjectType = Record<string, ErrorMessageType>;

interface FormItemProps {
  /** Omit for a label-less field (e.g. when a surrounding section header already labels it). */
  label?: ReactNode;
  children: ReactNode;
  error?: ReactNode;
  warning?: ReactNode;
  /**
   * Supporting text drawn in the field's message slot — the same place an error
   * would appear — and replaced by one the moment there is something to say.
   * For a running total or a "if each is different, click here" aside that
   * belongs to the field rather than to its label (which is what `description`
   * is for).
   */
  hint?: ReactNode;
  tooltip?: ReactNode;
  description?: string | undefined;
  /** When true, dims the label to indicate the field is inactive. */
  disabled?: boolean | undefined;
  /**
   * Fill the width of the container. Off by default (the field is only as wide
   * as its control). Opt in when the field sits in a flex-start form column and
   * should span it — otherwise it shrinks to its content width. The control
   * inside still governs its own width via its own `width` prop.
   */
  fillWidth?: boolean | undefined;
  /**
   * A node rendered right-aligned on the label row, opposite the label — e.g. a
   * "Forgot your password?" link beside a "Password" label, or an "Optional"
   * marker. Only meaningful with the top-label layout.
   */
  labelAction?: ReactNode | undefined;
  /**
   * Render a prominent label (base size, semibold, full-contrast) instead of
   * the default small muted one. For customer-facing forms (login, sign-up)
   * where the label is the primary field affordance, not the dense admin look.
   */
  emphasizeLabel?: boolean | undefined;
  /**
   * The reason behind `warning`, shown on the control's in-field warning mark
   * rather than in the message line, and opened while the control has focus so
   * nobody has to discover the mark is hoverable. Use it when the explanation is
   * a sentence: these fields can be ~72px wide, so a sentence in the line below
   * wraps to five rows and pushes into whatever follows. The short `warning`
   * names the problem ("Too long?"); this says why.
   *
   * Honored by `TextInput`, `Dropdown` and `MultiSelect` — the controls with an
   * end slot to put a mark in. **`TextArea` ignores it**: it renders a bare
   * `<textarea>`, and the relative wrapper a mark would need changes how `grow`
   * / `flex-1` size it. A textarea is also wide enough for the sentence to just
   * go in `warning`.
   */
  warningExplanation?: ReactNode | undefined;
}

function FeedbackMessage_({
  error,
  warning,
  hint,
  reserveSpace,
}: {
  error?: ReactNode;
  warning?: ReactNode | undefined;
  hint?: ReactNode | undefined;
  /** Reserve vertical space for the message even when not shown, to prevent layout shift */
  reserveSpace?: boolean;
}) {
  // No leading icon — the status icon lives in the field itself (see the
  // control's own error adornment), so the message below is plain text.
  //
  // A warning message is plain foreground text — the gold icon in the field
  // carries the intent, so the words only have to be readable. Tinting them
  // gold instead fails contrast (2.08:1 on white), and the darker
  // `warning-strong` that would pass reads muddy across a whole line.
  const message = error ? (
    <span className="text-red">{error}</span>
  ) : warning ? (
    <span className="text-foreground">{warning}</span>
  ) : hint ? (
    <span className="text-content-secondary">{hint}</span>
  ) : undefined;

  if (!message && !reserveSpace) {
    // oxlint-disable-next-line fs/no-null -- React requires null for conditional rendering
    return null;
  }

  // One line's worth of space reserved, so a message appearing doesn't shift the
  // form — but `min-h`, not `h`: a message longer than one line grows the slot
  // and pushes what follows down, which is what Cargo did.
  //
  // This used to be a fixed `h-4` with the words absolutely positioned inside.
  // That keeps a wrapped message from moving the form, but it has nowhere to put
  // the overflow, so it draws on top of the next section. The dock-hours warning
  // carries a hard `<br />` and is therefore always two lines — it rendered
  // across "Loading Capabilities" and across the neighbouring field's message.
  // Overlapping text is worse than a form that grows.
  return <div className="mt-1 min-h-4 text-xs">{message}</div>;
}

function LabelText_({
  text,
  tooltip,
  description,
}: {
  text: ReactNode;
  tooltip?: ReactNode;
  description?: string | undefined;
}) {
  return (
    <>
      {/* The label word renders on the brand headline face (Neue Haas Unica),
          matching buttons and headings. Scoped to the text span so a label-row
          action (e.g. a "Forgot your password?" link button) keeps the body
          font. */}
      {/* Plain text flow, no flex: the medallion brings its own left margin and
          aligns itself, so a label here spaces and sits exactly as one inline in
          a sentence does — and a long label can still wrap. */}
      <span style={headingFontStyle()}>
        {text}
        {tooltip && <Tooltip content={tooltip} variant="info" />}
      </span>
      {description && (
        <span className="mt-0.5 block text-xs font-normal text-content-muted">
          {description}
        </span>
      )}
    </>
  );
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function FormItem({
  label,
  children,
  error,
  warning,
  hint,
  tooltip,
  description,
  disabled,
  fillWidth,
  labelAction,
  emphasizeLabel,
  warningExplanation,
}: FormItemProps) {
  const { labelPosition, labelAlign } = useLabelLayout();
  // Underline fields carry their own top padding, so the label can tuck tight
  // against them; boxed fields don't, so the label needs a real gap.
  const presentation = useControlPresentation();
  const [touched, setTouched] = useState(false);
  const [timedReveal, setTimedReveal] = useState(false);
  const submitted = useIsFormSubmitted();

  // Debounced time-based reveal: each keystroke restarts the timer, and when
  // the typing pauses the error is unlocked (sticky).
  const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => {
    return () => clearTimeout(revealTimer.current);
  }, []);
  const handleInput = useCallback(() => {
    clearTimeout(revealTimer.current);
    revealTimer.current = setTimeout(
      () => setTimedReveal(true),
      DONE_TYPING_AFTER_MS,
    );
  }, []);

  // Opening a Dropdown, MultiSelect or date picker moves focus into a surface
  // that is portaled out of this subtree, so the trigger blurs and that blur
  // bubbles through here — which read as "left the field without answering"
  // and painted the field red the instant it was clicked into. The field is
  // being used, not left, and the trigger says so: it stays `aria-expanded`
  // for as long as its surface is open. Leave the field settled until it
  // closes; a later blur or the submit still reveals the error.
  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.querySelector('[aria-expanded="true"]')) {
      return;
    }
    setTouched(true);
  }, []);

  // Only show error after the field has been blurred, the form was submitted,
  // or the typing paused — never on mount, so a fresh add-dialog with empty
  // required fields doesn't open covered in "required" errors. Pre-populated
  // invalid values still surface on the first save attempt.
  const showError = !!(error && (touched || submitted || timedReveal));
  const visibleError = showError ? error : undefined;
  // The control's status drives its border/underline and its in-field status
  // icon: a visible error wins, otherwise a warning, otherwise neutral.
  const status: FieldStatus = showError
    ? "error"
    : warning
      ? "warning"
      : undefined;
  const statusValue = useMemo(
    () => ({ status, warningMessage: warning, warningExplanation }),
    [status, warning, warningExplanation],
  );

  const wrappedChildren = (
    <FormItemErrorProvider value={statusValue}>
      {/* beside: center the control in its cell so short inline controls
          (e.g. a Switch, which aligns to the text baseline otherwise) sit level
          with the label instead of a hair high. Full-width controls are
          unaffected (align-items is cross-axis only). */}
      {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- blur/input bubble from child controls */}
      <div
        onBlur={handleBlur}
        onInput={handleInput}
        className={labelPosition === "beside" ? "flex items-center" : undefined}
      >
        {children}
      </div>
    </FormItemErrorProvider>
  );

  if (labelPosition === "beside") {
    // The message sits under the control in the same cell, so the grid would
    // center the label on control + message and drop a one-line label below
    // the control's middle. Padding that label by one message line (`mt-1` +
    // `min-h-4`) centers it on the control alone, and it stays put when a
    // message appears: the row and the label's box grow by the same line.
    // A label with a description is left centered on control + message. With
    // a two-line description the label is about as tall as that pair, so the
    // title sits level with the control's top edge, which is where it reads as
    // aligned; padding it would lift the title above the control. A message
    // that wraps grows past the padding, so a one-line label drifts half a
    // line; that's rare and far smaller than the miss it replaces.
    const padLabel = !!(visibleError || warning || hint) && !description;
    return (
      <>
        {/* crisp, dark label (structure comes from the differentiated
            section headers, not from dimming the labels). No top padding — the
            grid centers the label with its control. other layouts keep a top nudge
            to sit level with the boxed input. */}
        <span
          className={`${labelAlign === "left" ? "text-left" : "text-right"} text-[13px] font-medium ${disabled ? "text-faint" : "text-foreground"}${padLabel ? " pb-5" : ""}`}
        >
          <LabelText_
            text={label}
            tooltip={tooltip}
            description={description}
          />
        </span>
        <div className="min-w-0">
          {wrappedChildren}
          <FeedbackMessage_
            error={visibleError}
            warning={warning}
            hint={hint}
          />
        </div>
      </>
    );
  }

  return (
    <div className={fillWidth ? "w-full" : undefined}>
      {/* The Cargo-derived: a lighter, medium-weight label. Underline fields carry
          their own top padding, so a negative margin tucks the label tight onto
          them; boxed fields have no such padding, so the label needs a real gap
          or it jams onto the box.  */}
      {(label || description || labelAction) && (
        <div
          className={`${
            emphasizeLabel
              ? `mb-1 text-base font-semibold ${disabled ? "text-faint" : "text-foreground"}`
              : `${presentation === "underlined" ? "-mb-1" : "mb-1"} text-xs font-normal ${disabled ? "text-faint" : "text-muted-foreground"}`
          }${labelAction ? " flex items-baseline justify-between gap-2" : ""}`}
        >
          {labelAction ? (
            // One flex child, not two: the row is `flex justify-between` only
            // when there's an action, and LabelText_ renders the label and the
            // description as siblings. Unwrapped, the description becomes a
            // third flex item and gets spread onto the label's baseline instead
            // of sitting under it. `min-w-0` lets a long description wrap
            // rather than push the action off the row.
            <div className="min-w-0">
              <LabelText_
                text={label}
                tooltip={tooltip}
                description={description}
              />
            </div>
          ) : (
            <LabelText_
              text={label}
              tooltip={tooltip}
              description={description}
            />
          )}
          {labelAction}
        </div>
      )}
      {wrappedChildren}
      <FeedbackMessage_
        error={visibleError}
        warning={warning}
        hint={hint}
        reserveSpace
      />
    </div>
  );
}
